'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'

export type Language = 'en' | 'hi' | 'mr'

interface LanguageContextProps {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined)

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    'brand.title': 'Swaastha AI',
    'brand.tagline': 'Nutrition Assistant',
    'brand.subtagline': 'Command Center',
    'section.swaastha_ai': 'Swaastha AI',
    'section.my_kitchen': 'My Kitchen',
    'section.administration': 'Administration',

    'nav.home': 'Home',
    'nav.recipe_generator': 'Recipe Generator',
    'nav.centres': 'Nearby Centres',
    'nav.about': 'About Swaastha AI',
    'nav.our_mission': 'Our Mission',
    'nav.developer_api': 'Developer API',
    'nav.recipe_history': 'Recipe History',
    'nav.saved_recipes': 'Saved Recipes',
    'nav.select_lang': 'Language',
    'nav.dashboard': 'Dashboard',
    'nav.collapse': 'Collapse',
    'nav.expand_sidebar': 'Expand sidebar',
    'nav.search': 'Search...',
    'nav.ai_manager': 'AI Manager',

    'auth.signin': 'Sign In / Sign Up',
    'auth.signout': 'Sign out',
    'auth.profile': 'Profile',
    'auth.settings': 'Settings',
    'auth.sso_notice': 'Managed by Keycloak SSO',

    'hero.tagline': 'AI-Powered Nutrition Assistant',
    'hero.headline_part1': 'Transform Ingredients',
    'hero.headline_part2': 'Into Balanced Meals',
    'hero.subheadline':
      'Swaastha AI helps families fight malnutrition by converting locally available ingredients into healthy, balanced recipes.',
    'hero.feat.malnutrition': 'Combat Malnutrition',
    'hero.feat.health': 'Nutrition Focused',
    'hero.feat.ai': 'AI-Powered Recipes',

    'input.title': 'Add Your Available Ingredients',
    'input.subtitle': 'Type, speak, or upload an image of your ingredients.',
    'input.placeholder': 'e.g. Potato, Spinach, Rice',
    'input.add': 'Add',
    'input.voice': 'Speak Ingredients',
    'input.voice.listening': 'Listening... Speak now',
    'input.image': 'Upload Image',
    'input.image.analyzing': 'Analyzing image...',
    'input.clear': 'Clear All',
    'input.min_warning': 'Add at least 1 ingredient to generate recipes.',
    'input.generate': 'Generate Recipes',
    'input.generating': 'Generating Recipes...',

    'recipe.easy': 'Easy',
    'recipe.medium': 'Medium',
    'recipe.hard': 'Hard',
    'recipe.prep': 'Prep',
    'recipe.cook': 'Cook',
    'recipe.servings': 'Servings',
    'recipe.mins': 'mins',
    'recipe.show_full': 'Show Full Recipe',
    'recipe.hide_full': 'Hide Full Recipe',
    'recipe.read_aloud': 'Read Aloud',
    'recipe.stop_aloud': 'Stop Audio',
    'recipe.save': 'Save Recipe',
    'recipe.saved': 'Saved',
    'recipe.youtube': 'Watch Video Tutorial on YouTube',
    'recipe.no_recipes': 'No recipes generated yet. Add ingredients and click Generate!',

    'centres.title': 'Find Nearby Support Centres',
    'centres.subtitle':
      'Connect with local ASHA workers, Anganwadi centres, and NGOs for on-ground health support.',
    'centres.permission_prompt':
      'Please allow location access to find centres near you, or enter coordinates manually.',
    'centres.slider_label': 'Search Radius',
    'centres.search_btn': 'Find Support Centres',
    'centres.searching': 'Searching Map...',
    'centres.filter_all': 'All Support',
    'centres.filter_anganwadi': 'Anganwadi',
    'centres.filter_asha': 'ASHA',
    'centres.filter_ngo': 'NGO',
    'centres.no_results': 'No centres found within this radius. Try expanding your search area.',
    'centres.distance': 'away',
    'centres.services': 'Services Provided:',
    'centres.get_directions': 'Get Directions on Google Maps',

    'about.hero.headline': 'Fighting Malnutrition',
    'about.hero.subtitle': 'One Recipe at a Time.',
    'about.hero.description':
      'Swaastha AI is an open, zero-barrier platform designed to assist families in transforming simple available ingredients into balanced, high-nutrition meals.',
    'about.stats.affected.val': '190M',
    'about.stats.affected.label': 'Children Affected',
    'about.stats.affected.desc':
      'Suffer from preventable malnutrition globally under age 5.',
    'about.stats.mortality.val': '45%',
    'about.stats.mortality.label': 'Child Mortality Link',
    'about.stats.mortality.desc':
      'Of early deaths are linked directly to nutritional deficiencies.',
    'about.stats.preventable.val': '70%',
    'about.stats.preventable.label': 'Preventable Cases',
    'about.stats.preventable.desc':
      'Of malnutrition cases can be averted through basic nutritional knowledge.',
    'about.impact.title': 'The Impact of Malnutrition',
    'about.impact.stunted.title': 'Stunted Growth',
    'about.impact.stunted.desc':
      'Deficiencies in vital nutrients like protein and zinc restrict skeletal development, preventing children from achieving their full physical growth potential.',
    'about.impact.immunity.title': 'Weakened Immunity',
    'about.impact.immunity.desc':
      'A lack of essential vitamins suppresses immune cell production, making children highly susceptible to infectious diseases and common seasonal ailments.',
    'about.impact.cognitive.title': 'Impaired Cognitive Development',
    'about.impact.cognitive.desc':
      'Anemia and iron deficiency during early years permanently stunt brain development, reducing future focus, learning capacity, and school performance.',
    'about.impact.economic.title': 'Economic Impact',
    'about.impact.economic.desc':
      'Long-term cognitive and physical stunting reduces future earning potential for families, locking generations into cycles of economic vulnerability.',
    'about.pillars.title': 'Our Three Pillars',
    'about.pillars.p1.title': '1. Smart Recipe Generation',
    'about.pillars.p1.desc':
      'Instantly converts whatever ingredients are currently in the kitchen into practical, nutritious local recipes.',
    'about.pillars.p2.title': '2. Nutrition-First',
    'about.pillars.p2.desc':
      'AI model explicitly weights micronutrients, proteins, and minerals to counteract childhood deficiency patterns.',
    'about.pillars.p3.title': '3. Resource Network',
    'about.pillars.p3.desc':
      'Bridges digital support with on-ground reality by mapping nearby ASHA workers and local health NGOs.',
    'about.cta.btn': 'Start Generating Recipes',
  },
  hi: {
    'brand.title': 'स्वास्थ्य एआई',
    'brand.tagline': 'पोषण सहायक',
    'brand.subtagline': 'कमांड सेंटर',
    'section.swaastha_ai': 'स्वास्थ्य एआई',
    'section.my_kitchen': 'मेरी रसोई',
    'section.administration': 'प्रशासन',

    'nav.home': 'मुख्य पृष्ठ',
    'nav.recipe_generator': 'रेसिपी जनरेटर',
    'nav.centres': 'निकटतम स्वास्थ्य केंद्र',
    'nav.about': 'हमारे बारे में',
    'nav.our_mission': 'हमारा उद्देश्य',
    'nav.developer_api': 'डेवलपर API',
    'nav.recipe_history': 'रेसिपी इतिहास',
    'nav.saved_recipes': 'सुरक्षित रेसिपी',
    'nav.select_lang': 'भाषा',
    'nav.dashboard': 'डैशबोर्ड',
    'nav.collapse': 'छोटा करें',
    'nav.expand_sidebar': 'साइडबार बड़ा करें',
    'nav.search': 'खोजें...',
    'nav.ai_manager': 'एआई मैनेजर',

    'auth.signin': 'लॉगिन / रजिस्टर',
    'auth.signout': 'लॉगआउट',
    'auth.profile': 'प्रोफ़ाइल',
    'auth.settings': 'सेटिंग्स',
    'auth.sso_notice': 'Keycloak SSO द्वारा प्रबंधित',

    'hero.tagline': 'एआई-संचालित पोषण सहायक',
    'hero.headline_part1': 'घर की सामग्री को बदलें',
    'hero.headline_part2': 'संतुलित और पौष्टिक भोजन में',
    'hero.subheadline':
      'स्वास्थ्य एआई परिवारों को घर में उपलब्ध सामग्री से स्वस्थ और संतुलित व्यंजन बनाने में मदद करके कुपोषण से लड़ने में सहायता करता है।',
    'hero.feat.malnutrition': 'कुपोषण से लड़ाई',
    'hero.feat.health': 'पोषण पर ध्यान',
    'hero.feat.ai': 'एआई-संचालित रेसिपी',

    'input.title': 'घर में उपलब्ध सामग्री जोड़ें',
    'input.subtitle': 'सामग्री टाइप करें, बोलें, या उसकी तस्वीर अपलोड करें।',
    'input.placeholder': 'जैसे: आलू, पालक, चावल',
    'input.add': 'जोड़ें',
    'input.voice': 'सामग्री बोलें',
    'input.voice.listening': 'सुन रहा है... अब बोलें',
    'input.image': 'तस्वीर अपलोड करें',
    'input.image.analyzing': 'तस्वीर का विश्लेषण हो रहा है...',
    'input.clear': 'सभी मिटाएं',
    'input.min_warning': 'रेसिपी बनाने के लिए कम से कम १ सामग्री जोड़ें।',
    'input.generate': 'रेसिपी बनाएं',
    'input.generating': 'रेसिपी तैयार हो रही है...',

    'recipe.easy': 'आसान',
    'recipe.medium': 'मध्यम',
    'recipe.hard': 'कठिन',
    'recipe.prep': 'तैयारी',
    'recipe.cook': 'पकाना',
    'recipe.servings': 'मात्रा',
    'recipe.mins': 'मिनट',
    'recipe.show_full': 'पूरी रेसिपी देखें',
    'recipe.hide_full': 'रेसिपी छिपाएं',
    'recipe.read_aloud': 'रेसिपी सुनें',
    'recipe.stop_aloud': 'ऑडियो रोकें',
    'recipe.save': 'सुरक्षित करें',
    'recipe.saved': 'सुरक्षित है',
    'recipe.youtube': 'यूट्यूब पर वीडियो ट्यूटोरियल देखें',
    'recipe.no_recipes': 'अभी तक कोई रेसिपी नहीं बनी है। सामग्री जोड़ें और बटन दबाएं!',

    'centres.title': 'निकटतम सहायता केंद्र खोजें',
    'centres.subtitle':
      'जमीनी स्तर पर स्वास्थ्य सहायता के लिए स्थानीय आशा कार्यकर्ताओं, आंगनवाड़ी केंद्रों और गैर सरकारी संगठनों (NGO) से जुड़ें।',
    'centres.permission_prompt':
      'अपने आस-पास के केंद्रों को खोजने के लिए कृपया स्थान (GPS) की अनुमति दें।',
    'centres.slider_label': 'खोज का दायरा',
    'centres.search_btn': 'सहायता केंद्र खोजें',
    'centres.searching': 'मानचित्र खोज रहे हैं...',
    'centres.filter_all': 'सभी सहायता',
    'centres.filter_anganwadi': 'आंगनवाड़ी',
    'centres.filter_asha': 'आशा',
    'centres.filter_ngo': 'NGO',
    'centres.no_results': 'इस दायरे में कोई केंद्र नहीं मिला। खोज का दायरा बढ़ाकर देखें।',
    'centres.distance': 'दूर',
    'centres.services': 'प्रदान की जाने वाली सेवाएं:',
    'centres.get_directions': 'गूगल मैप्स पर रास्ता देखें',

    'about.hero.headline': 'कुपोषण के खिलाफ लड़ाई',
    'about.hero.subtitle': 'एक समय में एक रेसिपी।',
    'about.hero.description':
      'स्वास्थ्य एआई एक खुला, शून्य-बाधा मंच है जो परिवारों को साधारण उपलब्ध सामग्री को संतुलित, उच्च-पोषण वाले भोजन में बदलने में मदद करता है।',
    'about.stats.affected.val': '190M',
    'about.stats.affected.label': 'प्रभावित बच्चे',
    'about.stats.affected.desc':
      '५ साल से कम उम्र के बच्चे वैश्विक स्तर पर रोके जा सकने वाले कुपोषण से पीड़ित हैं।',
    'about.stats.mortality.val': '45%',
    'about.stats.mortality.label': 'बाल मृत्यु दर संबंध',
    'about.stats.mortality.desc':
      'शुरुआती बाल मृत्यु सीधे तौर पर पोषण संबंधी कमियों से जुड़ी होती है।',
    'about.stats.preventable.val': '70%',
    'about.stats.preventable.label': 'बचाव योग्य मामले',
    'about.stats.preventable.desc':
      'कुपोषण के मामलों को बुनियादी पोषण ज्ञान के माध्यम से टाला जा सकता है।',
    'about.impact.title': 'कुपोषण का प्रभाव',
    'about.impact.stunted.title': 'अवरुद्ध शारीरिक विकास (Stunted Growth)',
    'about.impact.stunted.desc':
      'प्रोटीन और जिंक जैसे महत्वपूर्ण पोषक तत्वों की कमी शारीरिक विकास को रोक देती है, जिससे बच्चे अपनी पूरी शारीरिक क्षमता हासिल नहीं कर पाते।',
    'about.impact.immunity.title': 'कमजोर रोग प्रतिरोधक क्षमता (Weakened Immunity)',
    'about.impact.immunity.desc':
      'आवश्यक विटामिनों की कमी से शरीर की रोग प्रतिरोधक क्षमता घट जाती है, जिससे बच्चे संक्रमण और मौसमी बीमारियों की चपेट में जल्दी आते हैं।',
    'about.impact.cognitive.title': 'अवरुद्ध मानसिक विकास (Impaired Cognitive Development)',
    'about.impact.cognitive.desc':
      'प्रारंभिक वर्षों में एनीमिया और आयरन की कमी मस्तिष्क के विकास को स्थायी रूप से प्रभावित करती है, जिससे ध्यान केंद्रित करने और सीखने की क्षमता घट जाती है।',
    'about.impact.economic.title': 'आर्थिक प्रभाव (Economic Impact)',
    'about.impact.economic.desc':
      'लंबे समय तक शारीरिक और मानसिक कमजोरी भविष्य की कमाई की क्षमता को कम करती है, जिससे पीढ़ियां आर्थिक रूप से कमजोर रह जाती हैं।',
    'about.pillars.title': 'हमारे तीन स्तंभ',
    'about.pillars.p1.title': '1. स्मार्ट रेसिपी जनरेशन',
    'about.pillars.p1.desc':
      'रसोई में मौजूद किसी भी सामग्री को तुरंत व्यावहारिक, पौष्टिक स्थानीय व्यंजनों में बदल देता है।',
    'about.pillars.p2.title': '2. पोषण-प्रथम दृष्टिकोण',
    'about.pillars.p2.desc':
      'एआई मॉडल बच्चों में पोषक तत्वों की कमी को दूर करने के लिए सूक्ष्म पोषक तत्वों, प्रोटीन और खनिजों को प्राथमिकता देता है।',
    'about.pillars.p3.title': '3. सामुदायिक सहायता नेटवर्क',
    'about.pillars.p3.desc':
      'आस-पास के आशा कार्यकर्ताओं और स्थानीय गैर सरकारी संगठनों को जोड़कर जमीनी स्तर पर सहायता प्रदान करता है।',
    'about.cta.btn': 'रेसिपी बनाना शुरू करें',
  },
  mr: {
    'brand.title': 'स्वास्थ एआई',
    'brand.tagline': 'पोषण सहाय्यक',
    'brand.subtagline': 'कमांड सेंटर',
    'section.swaastha_ai': 'स्वास्थ एआई',
    'section.my_kitchen': 'माझी रसोई',
    'section.administration': 'प्रशासन',

    'nav.home': 'मुख्य पृष्ठ',
    'nav.recipe_generator': 'रेसिपी जनरेटर',
    'nav.centres': 'जवळचे आरोग्य केंद्र',
    'nav.about': 'आमच्याबद्दल',
    'nav.our_mission': 'आमचे ध्येय',
    'nav.developer_api': 'डेव्हलपर API',
    'nav.recipe_history': 'रेसिपी इतिहास',
    'nav.saved_recipes': 'जतन केलेल्या रेसिपी',
    'nav.select_lang': 'भाषा',
    'nav.dashboard': 'डॅशबोर्ड',
    'nav.collapse': 'संकुचित करा',
    'nav.expand_sidebar': 'साइडबार विस्तारा',
    'nav.search': 'शोधा...',
    'nav.ai_manager': 'एआय मॅनेजर',

    'auth.signin': 'लॉगिन / नोंदणी',
    'auth.signout': 'बाहेर पडा',
    'auth.profile': 'प्रोफाईल',
    'auth.settings': 'सेटिंग्ज',
    'auth.sso_notice': 'Keycloak SSO द्वारे व्यवस्थापित',

    'hero.tagline': 'एआई-आधारित पोषण सहाय्यक',
    'hero.headline_part1': 'घरातील साहित्यातून बनवा',
    'hero.headline_part2': 'सकस आणि पौष्टिक जेवण',
    'hero.subheadline':
      'स्वास्थ एआय घरातील उपलब्ध साहित्यापासून सकस आणि संतुलित अन्नपदार्थ बनवून कुपोषणाविरुद्ध लढण्यास मदत करते.',
    'hero.feat.malnutrition': 'कुपोषणावर मात',
    'hero.feat.health': 'पोषणावर लक्ष',
    'hero.feat.ai': 'एआय-आधारित रेसिपी',

    'input.title': 'घरातील उपलब्ध साहित्य जोडा',
    'input.subtitle': 'साहित्य टाईप करा, बोला, किंवा फोटो अपलोड करा.',
    'input.placeholder': 'उदा. बटाटा, पालक, तांदूळ',
    'input.add': 'जोडा',
    'input.voice': 'साहित्य बोला',
    'input.voice.listening': 'ऐकत आहे... आता बोला',
    'input.image': 'फोटो अपलोड करा',
    'input.image.analyzing': 'फोटो तपासत आहे...',
    'input.clear': 'सर्व काढा',
    'input.min_warning': 'रेसिपी बनवण्यासाठी किमान १ साहित्य जोडणे आवश्यक आहे.',
    'input.generate': 'रेसिपी बनवा',
    'input.generating': 'रेसिपी तयार होत आहे...',

    'recipe.easy': 'सोपे',
    'recipe.medium': 'मध्यम',
    'recipe.hard': 'कठीण',
    'recipe.prep': 'तयारी',
    'recipe.cook': 'शिजवणे',
    'recipe.servings': 'प्रमाण',
    'recipe.mins': 'मिनिटे',
    'recipe.show_full': 'पूर्ण रेसिपी पहा',
    'recipe.hide_full': 'रेसिपी लपवा',
    'recipe.read_aloud': 'रेसिपी ऐका',
    'recipe.stop_aloud': 'ऑडिओ थांबवा',
    'recipe.save': 'जतन करा',
    'recipe.saved': 'जतन केले',
    'recipe.youtube': 'यूट्यूबवर व्हिडिओ कृती पहा',
    'recipe.no_recipes': 'अद्याप कोणतीही रेसिपी तयार नाही. साहित्य जोडा आणि बटन दाबा!',

    'centres.title': 'जवळचे मदत केंद्र शोधा',
    'centres.subtitle':
      'मदत मिळवण्यासाठी तुमच्या परिसरातील आशा सेविका, अंगणवाडी केंद्रे आणि स्वयंसेवी संस्थांशी (NGO) संपर्क साधा.',
    'centres.permission_prompt':
      'तुमच्या जवळील केंद्रे शोधण्यासाठी कृपया जीपीएस (GPS) परवानगी द्या.',
    'centres.slider_label': 'शोध क्षेत्र',
    'centres.search_btn': 'मदत केंद्र शोधा',
    'centres.searching': 'नकाशा शोधत आहे...',
    'centres.filter_all': 'सर्व मदत',
    'centres.filter_anganwadi': 'अंगणवाडी',
    'centres.filter_asha': 'आशा',
    'centres.filter_ngo': 'NGO',
    'centres.no_results': 'या अंतरामध्ये एकही केंद्र आढळले नाही. अंतर वाढवून पुन्हा शोधा.',
    'centres.distance': 'लांब',
    'centres.services': 'मिळणाऱ्या सेवा:',
    'centres.get_directions': 'गूगल मॅप्सवर रस्ता पहा',

    'about.hero.headline': 'कुपोषणाविरुद्ध लढा',
    'about.hero.subtitle': 'प्रत्येक रेसिपीसह एक पाऊल पुढे.',
    'about.hero.description':
      'स्वास्थ एआय हे एक खुले, विना अडथळा प्लॅटफॉर्म आहे जे कुटुंबांना घरातील साध्या साहित्यातून सकस आणि उच्च दर्जाचे पौष्टिक जेवण बनवण्यास मदत करते.',
    'about.stats.affected.val': '190M',
    'about.stats.affected.label': 'बाधित मुले',
    'about.stats.affected.desc':
      '५ वर्षांखालील मुले जगभरात टाळता येण्याजोग्या कुपोषणाने ग्रस्त आहेत.',
    'about.stats.mortality.val': '45%',
    'about.stats.mortality.label': 'बालमृत्यू दराशी संबंध',
    'about.stats.mortality.desc':
      'लहान मुलांचे मृत्यू थेट शरीरातील पोषणविषयक कमतरतेमुळे होतात.',
    'about.stats.preventable.val': '70%',
    'about.stats.preventable.label': 'टाळता येणारे प्रमाण',
    'about.stats.preventable.desc':
      'कुपोषणाची प्रकरणे मूलभूत पोषण ज्ञानाच्या माध्यमातून सहज टाळली जाऊ शकतात.',
    'about.impact.title': 'कुपोषणाचे दुष्परिणाम',
    'about.impact.stunted.title': 'अपुर्ण शारीरिक वाढ (Stunted Growth)',
    'about.impact.stunted.desc':
      'प्रथिने आणि झिंक सारख्या महत्त्वाच्या पोषक तत्वांच्या कमतरतेमुळे हाडांची आणि स्नायूंची वाढ खुंटते, ज्यामुळे मुलांची पूर्ण वाढ होत नाही.',
    'about.impact.immunity.title': 'कमकुवत रोगप्रतिकारक शक्ती (Weakened Immunity)',
    'about.impact.immunity.desc':
      'आवश्यक जीवनसत्त्वांच्या अभावामुळे शरीराची रोगप्रतिकारक शक्ती कमी होते, ज्यामुळे मुले संसर्गजन्य आणि हंगामी आजारांना लवकर बळी पडतात.',
    'about.impact.cognitive.title': 'अवरुद्ध मानसिक विकास (Impaired Cognitive Development)',
    'about.impact.cognitive.desc':
      'लहान वयात ॲनिमिया आणि लोहाच्या कमतरतेमुळे मेंदूची वाढ कायमची मंदावते, ज्यामुळे लक्ष केंद्रित करणे आणि शिकण्याची क्षमता कमी होते.',
    'about.impact.economic.title': 'आर्थिक नुकसान (Economic Impact)',
    'about.impact.economic.desc':
      'दीर्घकालीन शारीरिक आणि मानसिक कमकुवतपणामुळे भविष्यातील मिळकतीची क्षमता कमी होते, ज्यामुळे गरिबीचे दुष्टचक्र सुरू राहते.',
    'about.pillars.title': 'आमचे तीन मुख्य आधार',
    'about.pillars.p1.title': '१. स्मार्ट रेसिपी निर्मिती',
    'about.pillars.p1.desc':
      'स्वयंपाकघरातील उपलब्ध साहित्यापासून त्वरित सोपे आणि पौष्टिक पदार्थ सुचवते.',
    'about.pillars.p2.title': '२. पोषणाला प्राधान्य',
    'about.pillars.p2.desc':
      'एआय मॉडेल मुलांमधील पोषक तत्वांची कमतरता भरून काढण्यासाठी प्रथिने, जीवनसत्त्वे आणि खनिजांना प्राधान्य देते.',
    'about.pillars.p3.title': '३. मदत नेटवर्क',
    'about.pillars.p3.desc':
      'जवळच्या आशा सेविका आणि स्वयंसेवी संस्थांशी संपर्क जोडून देऊन जमिनी स्तरावर मदत पुरवते.',
    'about.cta.btn': 'रेसिपी बनवण्यास सुरुवात करा',
  },
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('en')

  useEffect(() => {
    const savedLang = localStorage.getItem('swaastha_language') as Language
    if (savedLang && (savedLang === 'en' || savedLang === 'hi' || savedLang === 'mr')) {
      setLanguage(savedLang)
    }
  }, [])

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang)
    localStorage.setItem('swaastha_language', lang)
  }

  const t = (key: string): string => {
    return translations[language][key] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}
